import React, { useCallback, useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { handleToggleFilter } from "store/slices/ui.slice";
import { SegmentEventENUM } from "utils/enums/segmentENUM";
import { cleanObject } from "utils/object";
import { filterPanel } from "../FilterContainer";
import Icon from "../Icon/Icon";
import { FilterItem } from "./FilterItem";

export type FilterItemProps = {
  icon?: string;
  label: string;
  name: string;
  placeholder?: string;
  hideOn?: boolean;
} & (FilterTypeSelectable | FilterTypeSearchable | RangeTypes | DividerTypes);

interface RangeTypes {
  type: "range";
}

interface DividerTypes {
  type: "divider";
  removeBorder?: boolean;
}

export interface Options {
  label: string;
  value: string | number;
}

interface FilterTypeSelectable {
  type: "selectable";
  options: Options[] | { label: string; options: Options[] }[];
  isMulti?: boolean;
}

interface FilterTypeSearchable {
  type: "searchable";
}

interface FilterProps {
  filters: FilterItemProps[];
  handleFilter?: (filters: any) => void;
}

export const Filter: React.FC<FilterProps> = (props) => {
  const { filterOpen } = useAppSelector((state) => state.ui);
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const defaultValues = props.filters.reduce((acc, item) => {
    acc[item.name] = item.type === "searchable" ? "" : [];
    return acc;
  }, {} as Record<string, any>);

  const form = useForm({
    defaultValues,
  });

  const isValidJSON = (str: string) => {
    try {
      JSON.parse(str);
      return true;
    } catch {
      return false;
    }
  };

  const { base, header, hiddeOnClose, button } = filterPanel({
    isOpen: filterOpen,
  });

  const sendTrack = useCallback(() => {
    const filters: any = form.watch();
    const eventEnum = SegmentEventENUM.ANALYTICS_FILTER_APPLIED;

    const analyticsPayload = {
      title: filters?.job_titles,
      industry: filters?.industries,
      location: filters?.locations_person,
      department: filters?.departments,
    };

    const cleanedFilters = cleanObject(analyticsPayload);
    window.analytics.track(eventEnum, cleanedFilters);
  }, [form.watch()]);

  const handleSubmitFilter = useCallback(() => {
    const filters = form.watch();
    const keys = Object.keys(filters);

    setSearchParams((prev) => {
      const startDate = prev.get("startDate");
      const endDate = prev.get("endDate");
      const tab = prev.get("tab");
      const page = prev.get("page");
      const newParams = new URLSearchParams();

      keys.forEach((key: any) => {
        const values: any = filters?.[key];
        if (values && (Array.isArray(values) ? values.length > 0 : values)) {
          if (Array.isArray(values)) {
            const hasValueProperty = values.some(
              (item: any) => item?.value !== undefined
            );
            if (hasValueProperty) {
              const valuesWithValue = values.map((item: any) => item.value);
              newParams.set(key, JSON.stringify(valuesWithValue));
            } else {
              newParams.set(key, JSON.stringify(values));
            }
          } else {
            newParams.set(key, values);
          }
        }
      });

      startDate && newParams.set("startDate", startDate);
      endDate && newParams.set("endDate", endDate);
      tab && newParams.set("tab", tab);
      page && newParams.set("page", page);

      return newParams;
    });

    sendTrack();
    props.handleFilter && props.handleFilter(filters);
  }, [form, searchParams, setSearchParams]);

  const watchedValues = form.watch();

  const appliedFilters = useMemo(() => {
    const values = form.watch();

    const result = Object.entries(values).reduce(
      (acc, [key, value]: [string, any]) => {
        const isFilled =
          (Array.isArray(value) && value.length > 0) ||
          (typeof value === "string" && value.trim() !== "") ||
          (value !== null &&
            value !== undefined &&
            typeof value !== "string" &&
            !Array.isArray(value));

        if (isFilled) {
          acc.total += 1;
          acc.filters.push({ key, value });
        }

        return acc;
      },
      {
        total: 0,
        filters: [] as { key: string; value: any }[],
      }
    );

    return result;
  }, [watchedValues]);

  const renderFilterValue = (
    filter: { key: string; value: any },
    index: number
  ) => {
    if (Array.isArray(filter.value)) {
      if (
        filter.key === "last_seen" &&
        filter.value.length === 2 &&
        typeof filter.value[0] === "string" &&
        typeof filter.value[1] === "string"
      ) {
        const label = `${filter.value[0]} to ${filter.value[1]}`;
        return (
          <button
            key={`${filter.key}-${index}`}
            onClick={() => handleRemoveFiled(filter.key, filter.value, label)}
            className="bg-primary-500 hover:bg-primary-400 flex items-center gap-1 whitespace-nowrap text-white-500 px-2 py-1 rounded-full text-xs"
          >
            {label}
            <Icon name="close" size={10} />
          </button>
        );
      }

      return filter.value.map((v: any, i: number) => {
        const label =
          typeof v === "object" && v !== null && "label" in v ? v.label : v;

        return (
          <button
            key={`${filter.key}-${i}`}
            onClick={() => handleRemoveFiled(filter.key, filter.value, label)}
            className="bg-primary-500 hover:bg-primary-400 flex items-center gap-1 whitespace-nowrap text-white-500 px-2 py-1 rounded-full text-xs"
          >
            {label}
            <Icon name="close" size={10} />
          </button>
        );
      });
    }

    if (typeof filter.value === "string" || typeof filter.value === "number") {
      return (
        <button
          key={`${filter.key}-${index}`}
          onClick={() =>
            handleRemoveFiled(filter.key, filter.value, String(filter.value))
          }
          className="bg-primary-500 hover:bg-primary-400 flex items-center gap-1 whitespace-nowrap text-white-500 px-2 py-1 rounded-full text-xs"
        >
          {filter.value}
          <Icon name="close" size={10} />
        </button>
      );
    }

    return null;
  };

  useEffect(() => {
    props.filters.map((filter) => {
      const params = searchParams.get(filter.name);
      const isStrigned = params && isValidJSON(params);
      const parsedParams = isStrigned && JSON.parse(params);

      if (parsedParams && filter.type === "selectable") {
        let data: any = [];
        filter?.options?.map((item: any) => {
          if (item?.options?.length > 0) {
            item?.options?.map((filterI: any) => {
              parsedParams.map((i: string) => {
                if (i == filterI.value) {
                  data.push(filterI);
                }
              });
            });
          } else {
            parsedParams.map((i: string) => {
              if (i == item.value) {
                data.push(item);
              }
            });
          }
        });
        form.setValue(`${filter.name}` as any, data);
      }

      if (filter.type === "searchable") {
        form.setValue(`${filter.name}` as any, params as any);
      }
    });
  }, [props.filters]);

  const handleRemoveFiled = (key: string, filters: any, value: string) => {
    if (key === "last_seen") {
      form.setValue(key, []);
    } else if (Array.isArray(filters)) {
      const newFilters = filters.filter((item: any) => {
        if (typeof item === "object" && item !== null && "label" in item) {
          return item.label !== value;
        }
        return item !== value;
      });
      form.setValue(key, newFilters);
    } else {
      form.setValue(key, "");
    }

    form.handleSubmit(handleSubmitFilter)();
  };

  return (
    <FormProvider {...form}>
      <div className="flex">
        <div
          className={base()}
          onClick={() => !filterOpen && dispatch(handleToggleFilter())}
        >
          <div className={header()}>
            <div className="flex gap-4 w-full items-center">
              <Icon
                name="filter"
                size={filterOpen ? 20 : 14}
                color="text-white-500"
              />
              <div className="flex flex-col whitespace-nowrap">
                <h1 className="text-sm font-semibold text-white-500">Filter</h1>
                <small className={hiddeOnClose()}>Apply filters</small>
              </div>
              {appliedFilters.total > 0 && !filterOpen && (
                <div className="flex p-1 px-3 rounded-md font-bold text-sm bg-primary-500 text-white-500">
                  {appliedFilters.total}
                </div>
              )}
              <button
                className={button()}
                onClick={() => filterOpen && dispatch(handleToggleFilter())}
              >
                <Icon
                  name={filterOpen ? "chevron-left" : "chevron-down"}
                  color="text-white-500"
                  size={14}
                />
              </button>
            </div>
          </div>

          {filterOpen && (
            <ul
              id="menu-parent"
              className="SearchMainFilter gap-[14px] flex flex-col"
            >
              {props.filters.map(
                (item) =>
                  !item.hideOn && (
                    <FilterItem
                      {...item}
                      key={item.name}
                      handleClearField={handleSubmitFilter}
                      handleSubmitFilter={handleSubmitFilter}
                    />
                  )
              )}
            </ul>
          )}
        </div>

        {appliedFilters.total > 0 && !filterOpen && (
          <div className="flex items-center justify-between p-4">
            <div className="flex items-center gap-2 flex-wrap w-full">
              {appliedFilters.filters.map((filter, index) =>
                renderFilterValue(filter, index)
              )}
            </div>
          </div>
        )}
      </div>
    </FormProvider>
  );
};
